/**
 * React Query hooks for the agent-process definition store
 * (`sdk.agentProcesses`).
 *
 * Follows the useAgentDefinitions pattern: auth/sdk-gated queries, mutations
 * invalidate the list keys.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type {
  AgentProcess,
  CreateAgentProcessRequest,
  UpdateAgentProcessRequest,
  StepTemplate,
  CopyToAccountRequest,
  CopyToAccountResult,
} from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';

/**
 * Sort processes for display: seeded system defaults first, then
 * account customs, each group alphabetical.
 */
export function sortProcessesForDisplay(processes: AgentProcess[]): AgentProcess[] {
  return [...processes].sort((a, b) => {
    if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

export function useAgentProcesses() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<AgentProcess[], Error>({
    queryKey: ['agent-processes'],
    queryFn: async () => sortProcessesForDisplay(await getSDK().agentProcesses.list()),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useAgentProcess(processId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<AgentProcess, Error>({
    queryKey: ['agent-processes', processId],
    queryFn: () => getSDK().agentProcesses.get(processId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && processId),
  });
}

export function useStepTemplates() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<StepTemplate[], Error>({
    queryKey: ['agent-process-step-templates'],
    queryFn: async () => {
      const templates = await getSDK().agentProcesses.steps.list();
      // System templates first, then account customs
      return [...templates].sort((a, b) => {
        if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
    },
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useCreateAgentProcess() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<AgentProcess, Error, CreateAgentProcessRequest>({
    mutationFn: (request) => getSDK().agentProcesses.create(request),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['agent-processes'] });
      }
    },
  });
}

export function useUpdateAgentProcess() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<AgentProcess, Error, { processId: string; updates: UpdateAgentProcessRequest }>({
    mutationFn: ({ processId, updates }) => getSDK().agentProcesses.update(processId, updates),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['agent-processes'] });
      }
    },
  });
}

export function useDeleteAgentProcess() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (processId) => getSDK().agentProcesses.delete(processId),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['agent-processes'] });
      }
    },
  });
}

/** Copy a system process or step template into the account library. */
export function useCopyToAccount() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<CopyToAccountResult, Error, CopyToAccountRequest>({
    mutationFn: (request) => getSDK().agentProcesses.copyToAccount(request),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['agent-processes'] });
        void queryClient.invalidateQueries({ queryKey: ['agent-process-step-templates'] });
      }
    },
  });
}
